import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Policy : famille "barème ou condition fixée une fois par l'organisation".
 *
 * Barème ou condition que l'organisation fixe une fois (annulation, no-show,
 * caution, retard, remboursement, fidélité) et que les apps appliquent
 * automatiquement, chaque version étant conservée avec ses dates. Construite sur
 * le modèle "famille + type" de la bible (patron : `asset`), et sœur de
 * `priceList` : là où priceList porte des règles de PRIX, policy porte des règles
 * de CONDITION non tarifaires.
 *
 * Le socle ne connaît pas la règle métier concrète ("annulation 48h", "no-show
 * facturé", "caution 30%") : il connaît la famille générique `policy`. Le
 * vocabulaire métier est porté par le discriminant `type` (chaîne libre déclarée
 * par l'app), et le champ fonctionnel gouverné par le barème par `domain`
 * (annulation, no-show, caution, fidélité, retard). Comme `holderKind` pour
 * credit, `domain` n'est PAS le discriminant : c'est un état métier borné qui
 * reste un champ à part, le `type` demeurant la sous-famille.
 *
 * Sens des références : une policy s'applique à PLUSIEURS pièces à la fois
 * (booking, attendance, payment, credit, priceList, invoice). On ne porte donc
 * aucun id unique vers ces familles ; le périmètre visé vit dans la liste d'ids
 * `appliesToRefs` (même doctrine que voucher.redeemedRefs), et les conditions
 * chiffrées dans `rules`. Aucun import d'un autre schéma de famille.
 *
 * Versionnage : `version` numérote l'itération et `effectiveFrom` date son
 * entrée en vigueur ; chaque version est un document conservé (pas d'écrasement).
 *
 * Accès : org-wide standard en v0 (même doctrine qu'asset). Rien de sensible
 * signalé sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `policyCoreFields`        : les champs MÉTIER seuls (sans les champs standard).
 *   - `PolicySchema`            : famille complète = champs standard + champs métier.
 *   - `PolicyCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille policy (hors champs standard du socle). */
export const policyCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "annulation", "no-show", "caution"). */
  type: z.string().min(1),
  /** Domaine fonctionnel gouverné par le barème (ex. "annulation", "no-show", "caution", "fidelite", "retard"). État métier borné, à part du discriminant `type`. Optionnel. */
  domain: z.string().min(1).optional(),
  /** Barème / conditions, liste structurée pilotée par l'app (condition -> effet). Optionnel. */
  rules: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Numéro d'itération de la version, chaque version étant conservée. Optionnel. */
  version: z.number().int().optional(),
  /** Date d'entrée en vigueur de cette version. Optionnel. */
  effectiveFrom: z.date().optional(),
  /** Pièces auxquelles la policy s'applique (ids booking, attendance, payment, credit, priceList, invoice). Optionnel. */
  appliesToRefs: z.array(z.string()).optional(),
} as const;

/** Schéma complet de la famille policy (champs standard + champs métier). */
export const PolicySchema = z.object({
  ...baseEntityFields,
  ...policyCoreFields,
});

/** Type d'une policy. */
export type Policy = z.infer<typeof PolicySchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const PolicyCreateInputSchema = z.object({
  ...policyCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une policy. */
export type PolicyCreateInput = z.infer<typeof PolicyCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const PolicyUpdateInputSchema = z
  .object({ ...policyCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'une policy. */
export type PolicyUpdateInput = z.infer<typeof PolicyUpdateInputSchema>;
